import React, { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import {
  useReactTable,
  getCoreRowModel as defaultGetCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  GroupingState,
  flexRender,
} from '@tanstack/react-table';
import { LoadingButton } from '@mui/lab';

import Axios from 'axios';

// MUI icons
import DownloadIcon from '@mui/icons-material/Download';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import ArrowRightOutlinedIcon from '@mui/icons-material/ArrowRightOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import { TablePagination } from '@mui/material';
import { Table as MaUTable, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

// Types
import { TableProps } from '../../../types/Tables';
import { AppConfig } from '../../../constants/Constants';

// Components
import TablePaginationActions from './components/TablePaginationActions';

// Hooks
import useTimeMode from '../../../hooks/useTimeMode';

// Styles (SCSS)
import './components/Table.scss';

const { CancelToken } = Axios;

type GlobalFilterProps = {
  globalFilter: string;
  setGlobalFilter: (value: string | undefined) => void;
  preGlobalFilteredRows?: any;
};

// Global Filter
const GlobalFilter = ({ globalFilter, setGlobalFilter }: GlobalFilterProps) => (
  <span>
    <input
      value={globalFilter || ''}
      onChange={(e) => setGlobalFilter(e.target.value || undefined)}
      placeholder='Search...'
      style={{
        fontSize: '13px',
        border: '1px solid #E5E7EB',
        borderRadius: 6,
        padding: '6px 10px',
        outline: 'none',
      }}
    />
  </span>
);

export const Table = <T extends object>({
  data,
  columns,
  initialState,
  defaultColumn: overridedDefaultColumn,
  totalRows = 0,
  pageCount = 0,
  loading = false,
  isExporting = false,
  serverSide,
  manualPagination,
  showTotalPageCount = true,
  showSearchBar = true,
  showColumnToggler = true,
  showRightAction = true,
  showAction = true,
  showPagination = true,
  showFooter = false,
  minRows = 10,
  fetchData,
  exportData,
  canExportData,
  renderRowSubComponent,
  renderHeaderAction,
  height,
  fillHeight = false,
  groupBy: controlledGroupBy,
  onReload,
  setSearch,
  onRowClick,
  getCoreRowModel,
  rowProps,
  onPaginationStateChange,
}: TableProps<T>): JSX.Element => {
  const sortStateRef = useRef<{ field?: string; cycle: number }>({ cycle: 0 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [grouping, setGrouping] = useState<GroupingState>(controlledGroupBy || []);

  // The app-wide Local/UTC preference, set from the toggle beside the breadcrumbs.
  // Cells read it off `table.options.meta` -- see `utils/columns/timeModeCell`.
  const { timeMode } = useTimeMode();

  const table = useReactTable({
    data,
    columns,
    columnResizeMode: 'onChange',
    initialState: {
      pagination: {
        pageSize: AppConfig.defaultPageSize,
      },
      ...initialState,
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      grouping,
    },
    meta: {
      timeMode,
    },
    defaultColumn: {
      size: 100,
      minSize: 30,
      maxSize: 400,
      ...overridedDefaultColumn,
    },
    manualFiltering: serverSide,
    manualSorting: serverSide,
    manualPagination: manualPagination || serverSide,
    manualGrouping: serverSide,
    pageCount: serverSide ? pageCount : undefined,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel ?? defaultGetCoreRowModel,
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGroupingChange: setGrouping,
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const currentRowCount = table.getRowModel().rows.length;

  const canPreviousPage = pageIndex > 0;
  const canNextPage = currentRowCount === pageSize;

  // Lets a caller persist the page it is on; pagination itself stays react-table's.
  useEffect(() => {
    onPaginationStateChange?.({ pageIndex, pageSize });
  }, [pageIndex, pageSize, onPaginationStateChange]);

  // resolvePageCount returns -1 when the endpoint sends no totalCount, i.e. the total is
  // unknown. MUI then gets count={-1} so it skips its page-range check, and the label
  // shows the row range on its own instead of "101–100 of 100".
  const serverTotalUnknown = Boolean(serverSide) && pageCount === -1;

  const refresh = () => {
    fetchData?.({ sorting, columnFilters, grouping });
    onReload?.();
  };

  const startExport = () => {
    exportData?.({ sorting, columnFilters, grouping });
  };

  const handleColumnSort = (columnId: string) => {
    const currentField = sortStateRef.current.field;
    let cycle = sortStateRef.current.cycle;

    if (currentField !== columnId) {
      cycle = 0;
      sortStateRef.current = { field: columnId, cycle: 0 };
    } else {
      cycle = (cycle + 1) % 3;
      sortStateRef.current.cycle = cycle;
    }

    if (cycle === 0) setSorting([{ id: columnId, desc: false }]);
    else if (cycle === 1) setSorting([{ id: columnId, desc: true }]);
    else setSorting([]);
  };

  const onSetGlobalFilter = (filterValue: string | undefined) => {
    const safeValue = filterValue ?? '';
    table.setGlobalFilter(safeValue);
    if (setSearch) setSearch({ global: safeValue });
  };

  const useDebouncedFetchData = (fetchData: any, delay = 200) => {
    return useMemo(() => debounce(fetchData, delay), [fetchData, delay]);
  };

  const onFetchDataDebounced = useDebouncedFetchData(fetchData, 200);

  useEffect(() => {
    if (!serverSide && fetchData) {
      fetchData({
        manualPageCount: Math.ceil(table.getRowModel().rows.length / table.getState().pagination.pageSize),
      });
    }
  }, [serverSide, fetchData, table.getRowModel().rows.length, table.getState().pagination.pageSize]);

  useEffect(() => {
    if (serverSide) {
      let isMounted = true;
      const source = CancelToken.source();

      const doFetch = async () => {
        const { pagination, sorting, globalFilter } = table.getState();
        try {
          await onFetchDataDebounced(
            {
              pageIndex: pagination.pageIndex,
              pageSize: pagination.pageSize,
              sortBy: sorting,
              words: globalFilter,
            },
            { cancelToken: source.token },
          );
        } catch (error) {
          if (!isMounted) return;
          console.error('Fetch error:', error);
        }
      };

      doFetch();

      return () => {
        isMounted = false;
        source.cancel();
        onFetchDataDebounced.cancel?.();
      };
    }
  }, [
    serverSide,
    onFetchDataDebounced,
    table.getState().pagination,
    table.getState().sorting,
    table.getState().globalFilter,
    table.getState().grouping,
  ]);

  const isFirstParamsEffectRef = useRef(true);

  useEffect(() => {
    if (isFirstParamsEffectRef.current) {
      isFirstParamsEffectRef.current = false;
      return;
    }
    table.setPageIndex(0);
  }, [fetchData]);

  const extraRows = useMemo(() => Math.max(minRows - table.getRowModel().rows.length, 0), [minRows, table.getRowModel()]);

  const scrollStyle: React.CSSProperties = height ? { height, maxHeight: height } : {};

  return (
    <div className={fillHeight ? 'ui-table ui-table--fill' : 'ui-table'}>
      {/* Toolbar (search + Refresh / Export) — above the card   */}

      {(showSearchBar || showRightAction || renderHeaderAction) && (
        <div className='ui-table__toolbar'>
          <div className='ui-table__toolbar-left'>
            {showSearchBar && <GlobalFilter globalFilter={table.getState().globalFilter} setGlobalFilter={onSetGlobalFilter} />}
            {renderHeaderAction &&
              renderHeaderAction({
                selectedFlatRows: table.getSelectedRowModel().rows,
                toggleAllRowsSelected: table.toggleAllRowsSelected,
              })}
          </div>

          {showRightAction && showAction && (
            <div className='ui-table__toolbar-right'>
              <button type='button' className='ui-table__btn ui-table__btn--secondary' onClick={refresh}>
                <ReplayOutlinedIcon />
                Refresh
              </button>

              {canExportData() && (
                <LoadingButton
                  className='ui-table__btn ui-table__btn--primary'
                  startIcon={<DownloadIcon />}
                  loading={isExporting}
                  loadingPosition='start'
                  onClick={startExport}
                  disableElevation
                  variant='contained'
                  sx={{
                    // strip MUI defaults so our SCSS drives the look
                    boxShadow: 'none',
                    textTransform: 'none',
                    minWidth: 0,
                    padding: 0,
                    background: 'transparent',
                    color: 'inherit',
                    '&:hover': { background: 'transparent', boxShadow: 'none' },
                  }}
                >
                  Export CSV
                </LoadingButton>
              )}
            </div>
          )}
        </div>
      )}

      {/* Table card                                                          */}

      <div className='ui-table__card'>
        <div className='ui-table__scroll' style={scrollStyle} data-loading={loading}>
          <MaUTable>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isSorted = header.column.getIsSorted();
                    const canSort = header.column.getCanSort();
                    return (
                      <TableCell
                        key={header.id}
                        data-sortable={canSort ? 'true' : 'false'}
                        onClick={() => {
                          if (canSort) handleColumnSort(header.column.id);
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span className='ui-table__sort-icon'>
                            {isSorted === 'desc' ? (
                              <ArrowDropDownIcon fontSize='inherit' />
                            ) : isSorted === 'asc' ? (
                              <ArrowDropUpIcon fontSize='inherit' />
                            ) : (
                              <UnfoldMoreIcon fontSize='inherit' />
                            )}
                          </span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>

            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={table.getVisibleFlatColumns().length} className='ui-table__empty'>
                    No data to display
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => {
                  const extraProps = rowProps ? rowProps(row) : {};
                  const { style: rowStyle, ...rowEventProps } = extraProps as {
                    style?: React.CSSProperties;
                    onMouseEnter?: React.MouseEventHandler;
                    onMouseLeave?: React.MouseEventHandler;
                  };

                  return (
                    <React.Fragment key={row.id}>
                      <TableRow
                        key={row.id}
                        {...rowEventProps}
                        style={{ cursor: onRowClick ? 'pointer' : 'default', ...rowStyle }}
                        hover={!!onRowClick}
                        onClick={onRowClick ? (event: React.MouseEvent<HTMLTableRowElement>) => onRowClick(row.original, event) : undefined}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {row.getCanExpand() ? (
                              <>
                                <span onClick={row.getToggleExpandedHandler()}>
                                  {row.getIsExpanded() ? <KeyboardArrowDownOutlinedIcon /> : <ArrowRightOutlinedIcon />}
                                </span>{' '}
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </>
                            ) : (
                              flexRender(cell.column.columnDef.cell, cell.getContext())
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                      {row.getIsExpanded() && renderRowSubComponent && (
                        <TableRow>
                          <TableCell colSpan={table.getVisibleFlatColumns().length}>
                            <div style={{ width: '80%' }} className='subcomponent-datatable-detail'>
                              {renderRowSubComponent({ row })}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}

              {/* Filler rows to keep the card from collapsing on small pages */}
              {[...Array(extraRows)].map((_, index) => (
                <TableRow key={`extra-${index}`} className='ui-table__extra-row'>
                  <TableCell colSpan={table.getVisibleFlatColumns().length} />
                </TableRow>
              ))}
            </TableBody>

            {showFooter && (
              <tfoot>
                {table.getFooterGroups().map((footerGroup) => (
                  <TableRow key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <TableCell key={header.id}>
                        {header.column.columnDef.footer
                          ? typeof header.column.columnDef.footer === 'function'
                            ? header.column.columnDef.footer({ header, column: header.column, table })
                            : header.column.columnDef.footer
                          : null}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </tfoot>
            )}
          </MaUTable>
        </div>

        {/* Pagination                                                        */}

        {/* Stays mounted on an empty page past the end: with an unknown total the user can */}
        {/* overshoot, and hiding the bar there would leave them with no way back. */}
        {(currentRowCount !== 0 || (serverSide && canPreviousPage)) && showPagination && (
          <div className='ui-table__pagination'>
            <TablePagination
              component='div'
              labelDisplayedRows={({ from, to, count }) => {
                if (serverTotalUnknown || (serverSide && !totalRows)) {
                  const { pageIndex, pageSize } = table.getState().pagination;
                  const currentRows = table.getRowModel().rows.length;
                  if (currentRows === 0) return '0–0 of 0';
                  const start = pageIndex * pageSize + 1;
                  const end = pageIndex * pageSize + currentRows;
                  return `${start}–${end}`;
                }
                return showTotalPageCount ? `${from}–${to} of ${count}` : `${from}–${to}`;
              }}
              labelRowsPerPage={AppConfig.pageSizeLabel}
              rowsPerPageOptions={
                table.options.initialState?.pagination?.pageSize === 100 ? AppConfig.pageSizeOptionsLargeOnly : AppConfig.pageSizeOptions
              }
              count={serverSide ? (serverTotalUnknown ? -1 : totalRows) : table.getRowModel().rows.length}
              rowsPerPage={table.getState().pagination.pageSize}
              page={table.getState().pagination.pageIndex}
              onPageChange={(_, newPage) => table.setPageIndex(newPage)}
              onRowsPerPageChange={(e) => table.setPageSize(Number(e.target.value))}
              ActionsComponent={() => (
                <div className='ui-table__page-actions'>
                  <TablePaginationActions
                    canPreviousPage={serverSide ? canPreviousPage : table.getCanPreviousPage()}
                    canNextPage={serverSide ? canNextPage : table.getCanNextPage()}
                    gotoPage={table.setPageIndex}
                    previousPage={table.previousPage}
                    nextPage={table.nextPage}
                    pageCount={table.getPageCount()}
                  />
                </div>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};
